import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { useTranslation } from '../../t';
import { useNavigate } from 'react-router-dom';
import withAuth from '../../HOC/withAuth';
import CustomPagination from '../../components/Pagination';
import ShareDialog from '../../components/ShareDialog';
import OrderInfo from '../../components/OrderInfo';
import ListSkeleton from '../../components/ListSkeleton';
import C from '../../colors';

function Icon({ name, size = 16 }) {
  const s = { width: size, height: size, fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' };
  switch (name) {
    case 'search':  return <svg viewBox="0 0 24 24" {...s}><circle cx="11" cy="11" r="6"/><path d="m20 20-4-4"/></svg>;
    case 'plus':    return <svg viewBox="0 0 24 24" {...s}><path d="M12 5v14M5 12h14"/></svg>;
    case 'drop':    return <svg viewBox="0 0 24 24" {...s}><path d="m6 9 6 6 6-6"/></svg>;
    default: return null;
  }
}

function PrimaryBtn({ children, onClick, icon }) {
  return (
    <button onClick={onClick} style={{
      fontFamily: C.sans, fontSize: 13, fontWeight: 500,
      padding: '9px 16px', borderRadius: 7,
      background: C.brand, color: '#fff', border: 'none',
      cursor: 'pointer',
      display: 'inline-flex', alignItems: 'center', gap: 7,
      boxShadow: `0 1px 0 ${C.brand2}, 0 6px 16px -8px rgba(217,79,146,.55)`,
      whiteSpace: 'nowrap',
    }}>
      {icon && <Icon name={icon} size={14}/>}
      {children}
    </button>
  );
}


function Kpi({ label, value, hint, accent, warn, mono, onClick, active, testId }) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      data-testid={testId}
      aria-pressed={onClick ? active : undefined}
      onClick={onClick}
      style={{
      background: '#fff',
      border: `1px solid ${active ? '#D28A45' : C.hair}`,
      borderRadius: 10,
      padding: '14px 16px',
      display: 'flex', flexDirection: 'column', gap: 4,
      textAlign: 'left',
      fontFamily: C.sans,
      cursor: onClick ? 'pointer' : 'default',
      boxShadow: active ? '0 0 0 2px rgba(210,138,69,.12)' : 'none',
    }}>
      <div style={{
        fontFamily: C.mono, fontSize: 10, letterSpacing: 1.4,
        textTransform: 'uppercase', color: C.ink3, fontWeight: 600,
      }}>
        {label}
      </div>
      <div style={{
        fontFamily: mono ? C.mono : C.serif,
        fontSize: mono ? 28 : 32, lineHeight: 1, fontWeight: mono ? 500 : 400,
        color: warn ? '#A53636' : accent ? C.brand2 : C.ink,
        fontVariantNumeric: 'tabular-nums',
        marginTop: 2,
      }}>
        {value}
      </div>
      <div style={{ fontSize: 11.5, color: C.ink3, marginTop: 2 }}>
        {hint}
      </div>
    </Tag>
  );
}

const selectStyle = {
  padding: '7px 12px',
  border: `1px solid ${C.hair2}`, borderRadius: 8,
  background: '#fff', fontFamily: C.sans,
  fontSize: 13, color: C.ink, cursor: 'pointer',
  outline: 'none',
  WebkitAppearance: 'none', appearance: 'none',
  backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path d='M1 1l4 4 4-4' stroke='%238A7D83' stroke-width='1.4' fill='none' stroke-linecap='round'/></svg>")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 10px center',
  paddingRight: '26px',
};

const SORT_OPTIONS = [
  ['updated', '↓ 最近更新'],
  ['created', '↓ 创建时间'],
  ['name',    '↓ 客户姓名'],
  ['progress','↓ 选片进度'],
];

const OrderList = inject('ordersStore', 'dialogStore', 'userStore', 'snackbarStore')(observer(({ ordersStore, dialogStore, userStore, snackbarStore }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [sort, setSort] = useState('updated');
  const [searchType, setSearchType] = useState('name');
  const [searchText, setSearchText] = useState('');

  const allOrders = ordersStore.orders;
  const totalPage = ordersStore.totalPage;
  const page = ordersStore.page;
  const orderStatusFilter = ordersStore.currentOrderStatus || 'all';
  const expiryStatusFilter = ordersStore.currentExpiryStatus || 'all';

  const handlePageChange = (event, value) => {
    ordersStore.setPage(value);
  };

  const handleAddOrder = () => {
    if (userStore.level === 0 && allOrders.length >= 1) {
      snackbarStore.showSnackbar(t("A free account is limited to the creation of only one order."), 'error');
    } else {
      navigate('/user/orders/create');
    }
  };

  const handleSearch = () => {
    ordersStore.setSearch(searchType, searchText);
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const onClickShare = useCallback((order) => {
    ordersStore.setShareDialogData(order);
  }, [ordersStore]);

  const onOrderDelete = useCallback((orderId) => {
    dialogStore.openDialog(t("Are you sure you want to delete this order?"), () => {
      ordersStore.deleteOrder(orderId);
    });
  }, [dialogStore, ordersStore, t]);

  useEffect(() => {
    ordersStore.fetchOrders();
  }, [ordersStore]);

  // Status filtering and pagination are server-side. The page only sorts the
  // returned result so it never hides a server result a second time.
  const statusCounts = ordersStore.stats.statusCounts || {};
  const expiryCounts = ordersStore.stats.expiryCounts || {};
  const totalPhotos = Number(ordersStore.stats.totalPhotos || 0);
  const totalSelected = Number(ordersStore.stats.totalSelected || 0);

  const counts = {
    all: statusCounts.all
      ?? (orderStatusFilter === 'all' ? ordersStore.pagination.totalItems : undefined),
    draft: statusCounts.draft
      ?? (orderStatusFilter === 'draft' ? ordersStore.pagination.totalItems : undefined),
    selecting: statusCounts.selecting
      ?? (orderStatusFilter === 'selecting' ? ordersStore.pagination.totalItems : undefined),
    submitted: statusCounts.submitted
      ?? (orderStatusFilter === 'submitted' ? ordersStore.pagination.totalItems : undefined),
    expiryAll: expiryCounts.all
      ?? (expiryStatusFilter === 'all' ? ordersStore.pagination.totalItems : undefined),
    none: expiryCounts.none
      ?? (expiryStatusFilter === 'none' ? ordersStore.pagination.totalItems : undefined),
    active: expiryCounts.active
      ?? (expiryStatusFilter === 'active' ? ordersStore.pagination.totalItems : undefined),
    expiring: expiryCounts.expiring
      ?? (expiryStatusFilter === 'expiring' ? ordersStore.pagination.totalItems : undefined),
    expired: expiryCounts.expired
      ?? (expiryStatusFilter === 'expired' ? ordersStore.pagination.totalItems : undefined),
  };

  const filteredOrders = useMemo(() => {
    let r = [...allOrders];

    // sort
    if (sort === 'created')  r.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    if (sort === 'name')     r.sort((a, b) => (a.customerName || '').localeCompare(b.customerName || '', 'zh'));
    if (sort === 'progress') {
      r.sort((a, b) => {
        const pa = (a.photoCount || 0) > 0 ? (a.selectedPhotoCount || 0) / a.photoCount : 0;
        const pb = (b.photoCount || 0) > 0 ? (b.selectedPhotoCount || 0) / b.photoCount : 0;
        return pb - pa;
      });
    }
    // default 'updated': keep original order from server

    return r;
  }, [allOrders, sort]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>

      {/* PAGE HEADER */}
      <header style={{
        padding: '32px 40px 24px',
        borderBottom: `1px solid ${C.hair}`,
        background: C.paper,
        position: 'relative',
        overflow: 'hidden',
        flexShrink: 0,
      }}>
        {/* soft brand wash */}
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background: `radial-gradient(800px 200px at 100% -50%, ${C.brand}24, transparent 70%)`,
        }}/>

        <div style={{
          display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
          gap: 24, position: 'relative',
        }}>
          <div>
            <div style={{
              fontFamily: C.mono, fontSize: 11, letterSpacing: 1.4,
              color: C.brand2, textTransform: 'uppercase', fontWeight: 600,
              marginBottom: 8,
            }}>
              Workspace · Orders
            </div>
            <h1 style={{
              margin: 0, fontFamily: C.serif, fontSize: 42, fontWeight: 400,
              lineHeight: 1.02, letterSpacing: -0.6,
            }}>
              {t('Order List')}
              <span style={{ color: C.ink3, fontSize: 22, marginLeft: 14, letterSpacing: 0 }}>
                · {ordersStore.pagination.totalItems} 个订单
              </span>
            </h1>
            <p style={{
              margin: '10px 0 0', fontSize: 13.5, color: C.ink2,
              maxWidth: 620, lineHeight: 1.55,
            }}>
              所有客户的选片项目都在这里。点击“继续”会直接进入订单当前阶段，不必判断下一步该做什么。
            </p>
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <PrimaryBtn icon="plus" onClick={handleAddOrder}>{t('Create Order')}</PrimaryBtn>
          </div>
        </div>

        {/* KPI strip */}
        <div style={{
          marginTop: 22,
          display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16,
          position: 'relative',
        }}>
          <Kpi
            label="选片中"
            value={Number.isFinite(counts.selecting) ? counts.selecting : '—'}
            hint={Number.isFinite(counts.submitted) ? `${counts.submitted} 个已提交` : '按订单状态统计'}
          />
          <Kpi label="原片总量" value={totalPhotos} hint="所有订单合计" mono/>
          <Kpi label="已选交付" value={totalSelected} accent hint={`${totalPhotos > 0 ? Math.round(totalSelected / totalPhotos * 100) : 0}% 已确认`}/>
          <Kpi
            label="即将到期"
            value={Number.isFinite(counts.expiring) ? counts.expiring : '—'}
            hint="7 天内需要关注"
            warn={counts.expiring > 0}
            active={expiryStatusFilter === 'expiring'}
            testId="orders-kpi-expiring"
            onClick={() => ordersStore.setExpiryStatusFilter('expiring')}
          />
        </div>
      </header>

      {/* TOOLBAR */}
      <div style={{
        padding: '14px 40px',
        borderBottom: `1px solid ${C.hair}`,
        display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
        background: C.paper2,
        flexShrink: 0,
      }}>
        {/* Independent faceted filters: order progress + share expiry. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <span style={{ fontSize: 11, color: C.ink3, whiteSpace: 'nowrap' }}>订单状态</span>
          <div style={{
            display: 'flex', gap: 2, padding: 3,
            background: '#fff', border: `1px solid ${C.hair}`, borderRadius: 8,
          }}>
            {[
              ['all',       '全部'],
              ['draft',     '未分享'],
              ['selecting', '选片中'],
              ['submitted', '已提交'],
            ].map(([k, l]) => (
              <button data-testid={`orders-filter-${k}`} key={k} onClick={() => {
                ordersStore.setOrderStatusFilter(k === 'all' ? null : k);
              }} aria-pressed={orderStatusFilter === k} style={{
                padding: '6px 12px', border: 'none', borderRadius: 5, cursor: 'pointer',
                background: orderStatusFilter === k ? C.ink : 'transparent',
                color: orderStatusFilter === k ? '#fff' : C.ink2,
                fontFamily: C.sans, fontSize: 13, fontWeight: 500,
                whiteSpace: 'nowrap',
              }}>
                {l}
                {Number.isFinite(counts[k]) && (
                  <span style={{ opacity: 0.55, fontVariantNumeric: 'tabular-nums', marginLeft: 5, fontFamily: C.mono }}>
                    {counts[k]}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <span style={{ fontSize: 11, color: C.ink3, whiteSpace: 'nowrap' }}>分享有效期</span>
          <div style={{
            display: 'flex', gap: 2, padding: 3,
            background: '#fff', border: `1px solid ${C.hair}`, borderRadius: 8,
          }}>
            {[
              ['all',      '全部', counts.expiryAll],
              ['none',     '未设置', counts.none],
              ['active',   '有效', counts.active],
              ['expiring', '即将过期', counts.expiring],
              ['expired',  '已过期', counts.expired],
            ].map(([k, l, count]) => {
              const isAttention = k === 'expiring' || k === 'expired';
              const selected = expiryStatusFilter === k;
              return (
                <button
                  data-testid={k === 'all' ? 'orders-expiry-filter-all' : `orders-filter-${k}`}
                  key={k}
                  onClick={() => ordersStore.setExpiryStatusFilter(k === 'all' ? null : k)}
                  aria-pressed={selected}
                  style={{
                    padding: '6px 12px', border: 'none', borderRadius: 5, cursor: 'pointer',
                    background: selected ? (isAttention ? '#A95F25' : C.ink) : 'transparent',
                    color: selected ? '#fff' : isAttention ? '#8B4D1F' : C.ink2,
                    fontFamily: C.sans, fontSize: 13, fontWeight: 500,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {l}
                  {Number.isFinite(count) && (
                    <span style={{ opacity: 0.6, fontVariantNumeric: 'tabular-nums', marginLeft: 5, fontFamily: C.mono }}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* sort */}
        <select data-testid="orders-sort" value={sort} onChange={e => setSort(e.target.value)} style={selectStyle}>
          {SORT_OPTIONS.map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>

        {/* search */}
        <div style={{
          display: 'flex', alignItems: 'center',
          marginLeft: 'auto', flexShrink: 0,
        }}>
          <div style={{
            display: 'flex', alignItems: 'center',
            border: `1px solid ${C.hair2}`,
            background: '#fff',
            borderRadius: 8,
            overflow: 'hidden',
          }}>
            <select data-testid="orders-search-type" value={searchType} onChange={e => setSearchType(e.target.value)} style={{
              border: 'none', background: 'transparent',
              padding: '7px 6px 7px 12px', fontFamily: C.sans,
              fontSize: 12, color: C.ink2, cursor: 'pointer', outline: 'none',
              borderRight: `1px solid ${C.hair}`,
            }}>
              <option value="name">{t('Customer Name')}</option>
              <option value="phone">{t('Customer Phone')}</option>
            </select>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '0 10px' }}>
              <Icon name="search" size={13}/>
              <input
                data-testid="orders-search-input"
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={searchType === 'name' ? t('Customer Name') : t('Customer Phone')}
                style={{
                  border: 'none', outline: 'none',
                  fontFamily: C.sans, fontSize: 13, padding: '8px 0',
                  width: 200, color: C.ink, background: 'transparent',
                }}
              />
              {searchText && <button data-testid="orders-search-clear" onClick={() => { setSearchText(''); ordersStore.setSearch(null, null); }} style={{
                border: 'none', background: 'none', color: C.ink3,
                cursor: 'pointer', padding: 0, fontSize: 16, lineHeight: 1,
              }}>×</button>}
            </div>
          </div>
        </div>
      </div>

      {/* LIST */}
      <section style={{
        flex: 1, overflow: 'auto',
        padding: '24px 40px 40px',
      }}>
        {ordersStore.isLoading ? (
          <ListSkeleton variant="rows" count={3}/>
        ) : filteredOrders.length === 0 ? (
          <div style={{
            padding: '80px 40px',
            textAlign: 'center',
            border: `1px dashed ${C.hair2}`,
            borderRadius: 14,
            background: C.paper2,
          }}>
            <div style={{ fontFamily: C.serif, fontSize: 28, color: C.ink2 }}>
              没有匹配的订单
            </div>
            <div style={{ marginTop: 6, fontSize: 13, color: C.ink3 }}>
              换个筛选条件，或者新建一个订单试试。
            </div>
            <div style={{ marginTop: 16 }}>
              <PrimaryBtn icon="plus" onClick={handleAddOrder}>{t('Create Order')}</PrimaryBtn>
            </div>
          </div>
        ) : (
          <>
            {filteredOrders.map(order => (
              <OrderInfo key={order.id} order={order} onOrderDelete={onOrderDelete} onClickShare={onClickShare} />
            ))}

            <div style={{
              marginTop: 16,
              textAlign: 'center',
              fontFamily: C.mono, fontSize: 11, color: C.ink3,
              letterSpacing: 1.2, textTransform: 'uppercase',
            }}>
              ─ End of {filteredOrders.length} orders ─
            </div>
          </>
        )}

        {filteredOrders.length > 0 && totalPage > 1 && (
          <div style={{ marginTop: 8, display: 'flex', justifyContent: 'center' }}>
            <CustomPagination count={totalPage} page={page} onChange={handlePageChange} />
          </div>
        )}
      </section>

      <ShareDialog />
    </div>
  );
}));

export default withAuth(OrderList);
